/**
 * Auth Normalizer
 * Transforms authentication API responses to frontend models.
 * @module api/normalizers/auth
 */

// LLM:METADATA
// :hierarchy: [Domain:Authentication | Data_Normalization | Service]
// :relates-to: uses: "responseNormalizer.js", required-by: "auth.client.js"
// :rationale: "Transforms backend auth responses to match frontend expectations (user object structure, field naming)"
// :contract: pre: "validated data from auth contract", post: "normalized auth response for useAuth composable", invariant: "always returns expected structure"
// :decision_cache: "Chose simple pass-through normalizer for auth since backend response structure already matches frontend needs [AUTH-NORM-001]"
// LLM:END

import { normalizeAccessProfile } from "./access.normalizer"

const normalizeAuthAccessProfile = (profile, permissionsVersion) => {
  if (!profile || typeof profile !== "object" || Array.isArray(profile)) {
    return null
  }

  const profileVersion = Number(profile.permissions_version)
  const fallbackVersion = Number(permissionsVersion)
  const effectiveVersion =
    Number.isFinite(profileVersion) && profileVersion > 0
      ? profileVersion
      : fallbackVersion

  return normalizeAccessProfile({
    ...profile,
    permissions_version: effectiveVersion,
  })
}

/**
 * Normalize auth login response
 * Transforms backend login response to frontend model structure
 *
 * @param {Object} response - Raw login response from API
 * @param {string} response.result - Result indicator
 * @param {string} [response.token] - Auth token
 * @param {Object} [response.user] - User object
 * @param {number} response.user.id - User ID
 * @param {string} response.user.email - User email
 * @param {string} [response.user.name] - User name
 * @param {string} [response.user.role] - User role
 * @param {string} [response.message] - Optional message
 * @returns {Object} Normalized login response
 * @returns {boolean} returns.success - Success indicator
 * @returns {string} [returns.token] - Auth token
 * @returns {number} [returns.id] - User ID
 * @returns {string} [returns.role] - User role
 * @returns {string} [returns.error] - Error message if failed
 * @postcondition Legacy backend auth failures with `status=0` or `ok=0` normalize to `{ success: false, error }`.
 * @postcondition Legacy backend auth successes with direct `id`/`role` fields keep the raw auth identity fields.
 * @mutates none
 */
export function normalizeAuthResponse(response) {
  // Handle error responses
  if (
    !response ||
    response.result === "error" ||
    response.status === 0 ||
    response.ok === 0
  ) {
    return {
      success: false,
      error: response?.message || response?.msg || "Authentication failed",
    }
  }

  // Handle successful login
  if (response.result === "success" && response.user) {
    return {
      success: true,
      token: response.token,
      id: response.user.id,
      role: response.user.role || null,
      accessProfile: normalizeAuthAccessProfile(
        response.user.access_profile,
        response.user.permissions_version
      ),
      permissionsVersion: response.user.permissions_version || 0,
      effectiveRole: response.user.effective_role || null,
      email: response.user.email,
      name: response.user.name || null,
    }
  }

  // Fallback for legacy response format (direct role/id fields)
  if (response.role || response.id) {
    return {
      success: true,
      token: response.token,
      id: response.id,
      role: response.role || null,
      accessProfile: normalizeAuthAccessProfile(
        response.access_profile,
        response.permissions_version
      ),
      permissionsVersion: response.permissions_version || 0,
      effectiveRole: response.effective_role || null,
    }
  }

  throw new Error(`Unknown auth response format: ${JSON.stringify(response)}`)
}

/**
 * Normalize auth token check response
 * Transforms backend token check response to frontend model
 *
 * @param {Object} response - Raw token check response from API
 * @param {string} response.result - Result indicator
 * @param {Object|null} response.user - User object if valid
 * @returns {Object} Normalized token check response
 * @returns {boolean} returns.checked - Token validity
 * @returns {Object|null} returns.user - User object if valid
 */
export function normalizeTokenCheckResponse(response) {
  if (!response) {
    return {
      checked: false,
      user: null,
    }
  }

  // Handle legacy format: { checked: true }
  // Handle new format: { result: "success", user: {...} }
  const isChecked =
    response.result === "success" ||
    response.checked === true ||
    response.valid === true

  return {
    checked: isChecked,
    user: response.user || null,
    id: response.id || response.user?.id || null,
    role: response.role || response.user?.role || null,
    accessProfile: normalizeAuthAccessProfile(
      response.access_profile || response.user?.access_profile,
      response.permissions_version || response.user?.permissions_version
    ),
    permissionsVersion:
      response.permissions_version || response.user?.permissions_version || 0,
    effectiveRole:
      response.effective_role || response.user?.effective_role || null,
  }
}
